import { getActiveSetBonuses, getSetPieceCounts, SET_BONUSES, SET_NAMES, type SetBonusDef, type SetBonusEffect } from '@/domain/itemSets';
import type { EquippedMap, ItemSetId } from '@/domain/types';
import './SetBonusBar.css';

const EFFECT_LABEL: Record<SetBonusEffect, (value: number) => string> = {
  atk_pct: (v) => `+${Math.round(v * 100)}% ATK`,
  def_pct: (v) => `+${Math.round(v * 100)}% DEF`,
  hp_pct:  (v) => `+${Math.round(v * 100)}% HP`,
  final_hour_dr: () => 'Final Hour: 50% DR <30% HP',
  second_wind:   () => 'Second Wind: 1× Revive @ 50% HP',
  // Legacy effect kinds — no items grant these any more, kept for type compat.
  execute_reset:    () => 'Execute-Reset bei Kill',
  reflect_dmg:      () => '25% Schaden-Reflect',
  terminal_tick:    () => '20% Miner-Tick bei Klick',
  market_premium:   () => '5% Nano-Chip bei Manip.',
  flat_drop_chance: (v) => `+${Math.round(v * 100)}% Drop-Chance`,
  boss_uptier:      (v) => `${Math.round(v * 100)}% Boss Up-Tier`,
};

const SET_TOTAL_PIECES: Record<ItemSetId, number> = {
  hollow_knight: 8,
  vault_knight: 8,
};

interface SetBonusBarProps {
  equipment: EquippedMap;
}

/** Format the bonus list for one threshold. Multiple bonuses at the same
 *  piece-count (e.g. 3pc atk_pct + 3pc final_hour_dr) get joined inline. */
function formatBonuses(bonuses: SetBonusDef[]): string {
  if (bonuses.length === 0) return '—';
  return bonuses.map((b) => EFFECT_LABEL[b.effect](b.value)).join(' · ');
}

export function SetBonusBar({ equipment }: SetBonusBarProps) {
  const counts = getSetPieceCounts(equipment);
  const activeBonuses = getActiveSetBonuses(equipment);

  const setsWithPieces = Object.entries(counts) as Array<[ItemSetId, number]>;
  if (setsWithPieces.length === 0) return null;

  return (
    <div className="set-bonus-bar">
      <div className="set-bonus-bar__title">◈ Set-Boni</div>
      {setsWithPieces.map(([setId, count]) => {
        const total = SET_TOTAL_PIECES[setId];
        const setDefs = SET_BONUSES.filter((b) => b.setId === setId);
        // Group definitions by piece-count threshold so we can show one chip
        // per breakpoint (a chip can list multiple effects sharing a tier,
        // e.g. 3pc atk_pct + 3pc final_hour_dr).
        const tiers = Array.from(new Set(setDefs.map((b) => b.pieces))).sort((a, b) => a - b);
        return (
          <div key={setId} className="set-entry">
            <div className="set-entry__header">
              <span className="set-entry__name">{SET_NAMES[setId]}</span>
              <span className={`set-entry__count ${count >= total ? 'set-entry__count--max' : count >= 2 ? 'set-entry__count--half' : ''}`}>
                {count}/{total}
              </span>
            </div>
            <div className="set-entry__bonuses">
              {tiers.map((tier) => {
                const tierDefs = setDefs.filter((b) => b.pieces === tier);
                const tierActive = activeBonuses.filter((b) => b.setId === setId && b.pieces === tier);
                const isActive = tierActive.length > 0;
                return (
                  <span
                    key={tier}
                    className={`set-bonus-chip ${isActive ? 'set-bonus-chip--active' : ''}`}
                  >
                    {tier}P: {formatBonuses(isActive ? tierActive : tierDefs)}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
